'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Leaf, Heart, Sparkles } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

export default function Slide27KeyMessage({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2318] via-[#1a3a2a]/50 to-[#0f2318]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(76,175,125,0.5)] to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[rgba(76,175,125,0.04)] blur-3xl" />

        {/* Decorative leaf elements */}
        <svg className="absolute top-[15%] left-[8%] opacity-[0.08] w-[200px] h-[200px]" viewBox="0 0 100 100" fill="none">
          <ellipse cx="50" cy="50" rx="18" ry="45" fill="#4caf7d" transform="rotate(-20 50 50)" />
        </svg>
        <svg className="absolute bottom-[15%] right-[8%] opacity-[0.08] w-[180px] h-[180px]" viewBox="0 0 100 100" fill="none">
          <ellipse cx="50" cy="50" rx="18" ry="45" fill="#a8d87a" transform="rotate(25 50 50)" />
        </svg>
        <svg className="absolute top-[30%] right-[12%] opacity-[0.05] w-[140px] h-[140px]" viewBox="0 0 100 100" fill="none">
          <ellipse cx="50" cy="50" rx="15" ry="40" fill="#e8a030" transform="rotate(-45 50 50)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center items-center text-center">
        <div className="fade-in-up mb-8">
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#4caf7d]" />
            <Leaf size={16} className="text-[#4caf7d]" />
            <span className="text-[12px] font-600 uppercase tracking-[0.15em] text-[#4caf7d] font-body">Key Message</span>
            <Leaf size={16} className="text-[#4caf7d]" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#4caf7d]" />
          </div>
        </div>

        <div className="fade-in-up max-w-4xl mb-6">
          <h2 className="font-display font-900 text-[clamp(28px,5vw,64px)] leading-[1.1] text-[#f0ede6] tracking-tight mb-3">
            Không chỉ là chuyến đi
          </h2>
          <div className="section-divider mx-auto my-6" />
          <h2 className="font-display font-800 text-[clamp(22px,4vw,52px)] leading-[1.15] tracking-tight">
            <span className="text-gradient-green">Là hành trình</span>{' '}
            <span className="text-gradient-amber">tạo giá trị thật</span>
          </h2>
        </div>

        <div className="fade-in-up max-w-2xl mb-12">
          <p className="font-body text-[clamp(15px,1.8vw,20px)] text-[#a0b8a8] leading-relaxed">
            Mỗi sinh viên rời Trị An với kỹ năng mới, nội dung thật, chứng nhận có giá trị, và một câu chuyện đáng kể — không phải một chuyến picnic hay một buổi tình nguyện hình thức.
          </p>
        </div>

        <div className="fade-in-up grid grid-cols-1 sm:grid-cols-3 gap-5 w-full max-w-3xl">
          {[
            {
              icon: <Leaf size={20} className="text-[#4caf7d]" />,
              label: 'Cho môi trường',
              value: 'Hành động thật',
              color: '#4caf7d',
            },
            {
              icon: <Heart size={20} className="text-[#e8a030]" />,
              label: 'Cho sinh viên',
              value: 'Giá trị thật',
              color: '#e8a030',
            },
            {
              icon: <Sparkles size={20} className="text-[#a8d87a]" />,
              label: 'Cho xã hội',
              value: 'Tác động thật',
              color: '#a8d87a',
            },
          ].map((item, i) => (
            <div
              key={`keymsg-${i}`}
              className="rounded-2xl p-6 text-center"
              style={{
                background: `linear-gradient(145deg, ${item.color}12 0%, ${item.color}04 100%)`,
                border: `1px solid ${item.color}25`,
              }}
            >
              <div
                className="p-3 rounded-xl inline-flex mb-3"
                style={{ background: `${item.color}18` }}
              >
                {item.icon}
              </div>
              <p className="font-body text-[12px] text-[#a0b8a8] mb-1 uppercase tracking-wider">{item.label}</p>
              <p className="font-display font-700 text-[17px]" style={{ color: item.color }}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}